import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, CalendarClock, Package, MessageSquare, Send, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/components/Navbar";

const STATUS = {
  afventer: { label: "Afventer", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  godkendt: { label: "Godkendt", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  "færdig": { label: "Afsluttet", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  afvist: { label: "Afvist", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

function StatusBadge({ s }) {
  const cfg = STATUS[s] || STATUS.afventer;
  return <Badge className={`border ${cfg.cls} font-medium`}>{cfg.label}</Badge>;
}

const fmt = (d) => (d ? new Date(d).toLocaleDateString("da-DK", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function CustomerPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [products, setProducts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const [b, p, m] = await Promise.all([
        api.get("/bookings/mine"), api.get("/products/mine"), api.get("/messages/mine"),
      ]);
      setBookings(b.data); setProducts(p.data); setMessages(m.data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    try {
      const { data } = await api.post("/messages", { body: msg });
      setMessages([...messages, data]);
      setMsg("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="J-Computer logo" className="h-9 w-9 rounded-lg object-cover ring-1 ring-slate-200" />
            <span className="text-lg font-black tracking-tighter font-display text-slate-900">Min portal</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-slate-600">Hej, {user?.name?.split(" ")[0]}</span>
            <Button variant="outline" onClick={doLogout} data-testid="portal-logout" className="rounded-full gap-2">
              <LogOut className="h-4 w-4" /> Log ud
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        {!user?.approved && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="pending-banner">
            Din konto afventer godkendelse. Du kan se dine bookinger, men beskeder åbnes efter godkendelse.
          </div>
        )}

        {products.filter((p) => p.has_agreement || p.type === "serviceaftale").length > 0 && (
          <div className="mb-6 grid gap-3" data-testid="active-agreements">
            {products.filter((p) => p.has_agreement || p.type === "serviceaftale").map((p) => {
              const exp = p.agreement_expiry || p.expiry_date;
              const expired = exp && new Date(exp) < new Date();
              return (
                <div key={p.product_id} data-testid={`agreement-${p.product_id}`}
                  className={`rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${expired ? "border-rose-200 bg-rose-50" : "border-blue-200 bg-blue-50"}`}>
                  <span className={`grid place-items-center h-12 w-12 rounded-xl text-white shrink-0 ${expired ? "bg-rose-600" : "bg-blue-600"}`}>
                    <ShieldCheck className="h-6 w-6" />
                  </span>
                  <div className="flex-1">
                    <p className={`text-xs font-semibold uppercase tracking-wider ${expired ? "text-rose-700" : "text-blue-700"}`}>
                      {expired ? "Udløbet serviceaftale" : "Aktiv serviceaftale"}
                    </p>
                    <p className="text-lg font-display font-black text-slate-900">{p.name}{p.agreement_years ? ` · ${p.agreement_years} års aftale` : ""}</p>
                    {p.description && <p className="text-sm text-slate-600">{p.description}</p>}
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-xs text-slate-500">{expired ? "Udløb" : "Udløber"}</p>
                    <p className={`font-semibold ${expired ? "text-rose-600" : "text-slate-900"}`}>
                      {exp ? fmt(exp) : "Ingen udløbsdato"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <h1 className="text-2xl sm:text-3xl font-display font-black tracking-tighter text-slate-900">Velkommen tilbage</h1>
        <p className="mt-1 text-slate-500">Følg dine bookinger, produkter og beskeder ét sted.</p>

        <Tabs defaultValue="bookings" className="mt-6">
          <TabsList className="bg-white border border-slate-200 h-auto p-1 rounded-xl">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">
              <CalendarClock className="h-4 w-4" /> Bookinger
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">
              <Package className="h-4 w-4" /> Produkter
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">
              <MessageSquare className="h-4 w-4" /> Beskeder
            </TabsTrigger>
          </TabsList>

          {/* BOOKINGS */}
          <TabsContent value="bookings" className="mt-5">
            {bookings.length === 0 ? (
              <EmptyState text="Du har ingen bookinger endnu." action={() => navigate("/booking")} actionText="Book service" />
            ) : (
              <div className="grid gap-4">
                {bookings.map((b) => (
                  <div key={b.booking_id} data-testid={`booking-card-${b.booking_id}`}
                    className="rounded-xl border border-slate-200 bg-white p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-display font-bold text-slate-900">{b.category}</h3>
                          <StatusBadge s={b.status} />
                        </div>
                        {b.device && <p className="text-sm text-slate-500 mt-0.5">{b.device}</p>}
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">{fmt(b.created_at)}</span>
                    </div>
                    <p className="mt-3 text-sm text-slate-600 whitespace-pre-line">{b.description}</p>
                    <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                      {b.preferred_date && <span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> {fmt(b.preferred_date)}</span>}
                    </div>
                    {b.admin_reply && (
                      <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3">
                        <p className="text-xs font-semibold text-blue-700 mb-1">Svar fra J-Computer</p>
                        <p className="text-sm text-slate-700">{b.admin_reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-5">
            {products.length === 0 ? (
              <EmptyState text="Ingen produkter eller ydelser registreret endnu." />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {products.map((p) => {
                  const expired = p.expiry_date && new Date(p.expiry_date) < new Date();
                  return (
                    <div key={p.product_id} data-testid={`product-card-${p.product_id}`}
                      className="rounded-xl border border-slate-200 bg-white p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge variant="outline" className="mb-2 border-slate-200 text-slate-500 capitalize">{p.type}</Badge>
                          <h3 className="font-display font-bold text-slate-900">{p.name}</h3>
                        </div>
                        {p.price != null && <span className="text-sm font-semibold text-slate-900">{p.price} kr.</span>}
                      </div>
                      {p.description && <p className="mt-2 text-sm text-slate-600">{p.description}</p>}
                      <div className="mt-3 flex items-center gap-4 text-xs">
                        <span className="text-slate-500">Købt: {fmt(p.purchase_date)}</span>
                        {p.expiry_date && (
                          <span className={expired ? "text-rose-600 font-medium" : "text-slate-500"}>
                            Udløber: {fmt(p.expiry_date)}{expired ? " (udløbet)" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages" className="mt-5">
            <div className="rounded-xl border border-slate-200 bg-white flex flex-col h-[520px]">
              <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="message-list">
                {messages.length === 0 ? (
                  <p className="text-center text-sm text-slate-400 mt-10">Ingen beskeder endnu. Skriv til J-Computer nedenfor.</p>
                ) : messages.map((m) => (
                  <div key={m.message_id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                      m.sender === "customer" ? "bg-blue-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"
                    }`}>
                      <p>{m.body}</p>
                      <p className={`text-[10px] mt-1 ${m.sender === "customer" ? "text-blue-100" : "text-slate-400"}`}>
                        {m.sender === "customer" ? "Dig" : "J-Computer"} · {fmt(m.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={sendMsg} className="border-t border-slate-200 p-3 flex gap-2">
                <Input value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="message-input"
                  placeholder={user?.approved ? "Skriv en besked…" : "Afventer godkendelse…"}
                  disabled={!user?.approved} className="bg-slate-50 focus-visible:ring-blue-600" />
                <Button type="submit" disabled={!user?.approved} data-testid="message-send"
                  className="rounded-lg bg-blue-600 hover:bg-blue-700 shrink-0"><Send className="h-4 w-4" /></Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function EmptyState({ text, action, actionText }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
      <p className="text-slate-500">{text}</p>
      {action && <Button onClick={action} className="mt-4 rounded-full bg-blue-600 hover:bg-blue-700">{actionText}</Button>}
    </div>
  );
}
